'use client'

import {useEffect} from 'react'

/**
 * Top-level error boundary — replaces the root layout when an error happens
 * before/in layout itself. Must include <html> and <body>; cannot rely on
 * globals.css being loaded (root layout never renders), so styles are inlined.
 */
type GlobalErrorProps = {
  error: Error & {digest?: string}
  reset: () => void
}

export default function GlobalError({error, reset}: GlobalErrorProps) {
  useEffect(() => {
    console.error('Global application error:', error)
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          padding: '4rem 1.5rem',
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
          color: '#111',
          background: '#fff',
        }}
      >
        <main style={{maxWidth: '672px', margin: '0 auto'}}>
          <h1 style={{fontSize: '2rem', fontWeight: 700, marginBottom: '1rem'}}>
            Something went wrong
          </h1>
          <p style={{color: '#555', lineHeight: 1.6, marginBottom: '1.5rem'}}>
            An unexpected error occurred. Please try again, or visit the homepage.
          </p>
          <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
            <button
              type="button"
              onClick={reset}
              style={{
                background: '#111',
                color: '#fff',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/'
              }}
              style={{
                color: '#111',
                border: '1px solid #111',
                background: 'transparent',
                padding: '0.75rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Go home
            </button>
          </div>
          {error.digest ? (
            <p style={{marginTop: '2rem', fontSize: '0.875rem', color: '#888'}}>
              Reference code: <code style={{fontFamily: 'monospace'}}>{error.digest}</code>
            </p>
          ) : null}
        </main>
      </body>
    </html>
  )
}
